export interface InboundCommentPayload {
  kind: 'comment' | 'suggest';
  selection: string;
  body: string;
  pagePath: string;
  anchorId: string;
  nearestHeading?: string;
  contextSnippet?: string;
}

export type InboundMessage =
  | { type: 'kb-dashboard:comment'; payload: InboundCommentPayload }
  | { type: 'kb-dashboard:ready'; payload: { pagePath: string } }
  | { type: 'kb-dashboard:request-edit'; payload: { anchorId: string } }
  | { type: 'kb-dashboard:update-comment'; payload: { anchorId: string; body: string } };

export interface EditCommentPayload {
  anchorId: string;
  rawBody: string;
  kind: 'comment' | 'suggest';
  selection: string;
}

export type OutboundMessage =
  | { type: 'kb-dashboard:remove-highlight'; payload: { anchorId: string; pagePath?: string } }
  | { type: 'kb-dashboard:clear-highlights'; payload: Record<string, never> }
  | { type: 'kb-dashboard:edit-comment'; payload: EditCommentPayload };

const ALLOWED_ORIGIN_RE =
  /^https:\/\/[a-z0-9-]+\.grownby-knowledge-base\.pages\.dev$/;

// Local dev: wrangler serves the dashboard on :8788 and Vite on :5173, but the
// article site the iframe actually loads runs on $CONDUCTOR_PORT, which differs
// per Conductor workspace (see scripts/setup-local.sh). Any localhost port is
// therefore allowed -- a remote attacker cannot obtain a localhost origin.
const LOCALHOST_ORIGIN_RE = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/;

export function isAllowedOrigin(origin: string): boolean {
  if (ALLOWED_ORIGIN_RE.test(origin)) return true;
  if (LOCALHOST_ORIGIN_RE.test(origin)) return true;
  return false;
}

export function parseInbound(data: unknown): InboundMessage | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as { type?: unknown; payload?: unknown };
  if (typeof obj.type !== 'string') return null;
  if (obj.type === 'kb-dashboard:comment') {
    const p = obj.payload as Partial<InboundCommentPayload> | undefined;
    if (!p) return null;
    if (p.kind !== 'comment' && p.kind !== 'suggest') return null;
    if (
      typeof p.selection !== 'string' ||
      typeof p.body !== 'string' ||
      typeof p.pagePath !== 'string' ||
      typeof p.anchorId !== 'string'
    ) return null;
    if (p.nearestHeading !== undefined && typeof p.nearestHeading !== 'string') return null;
    if (p.contextSnippet !== undefined && typeof p.contextSnippet !== 'string') return null;
    const payload: InboundCommentPayload = {
      kind: p.kind,
      selection: p.selection,
      body: p.body,
      pagePath: p.pagePath,
      anchorId: p.anchorId,
      ...(p.nearestHeading ? { nearestHeading: p.nearestHeading } : {}),
      ...(p.contextSnippet ? { contextSnippet: p.contextSnippet } : {}),
    };
    return { type: 'kb-dashboard:comment', payload };
  }
  if (obj.type === 'kb-dashboard:ready') {
    const p = obj.payload as { pagePath?: unknown } | undefined;
    if (!p || typeof p.pagePath !== 'string') return null;
    return { type: 'kb-dashboard:ready', payload: { pagePath: p.pagePath } };
  }
  if (obj.type === 'kb-dashboard:request-edit') {
    const p = obj.payload as { anchorId?: unknown } | undefined;
    if (!p || typeof p.anchorId !== 'string') return null;
    return { type: 'kb-dashboard:request-edit', payload: { anchorId: p.anchorId } };
  }
  if (obj.type === 'kb-dashboard:update-comment') {
    const p = obj.payload as { anchorId?: unknown; body?: unknown } | undefined;
    if (!p || typeof p.anchorId !== 'string' || typeof p.body !== 'string') return null;
    return { type: 'kb-dashboard:update-comment', payload: { anchorId: p.anchorId, body: p.body } };
  }
  return null;
}
